/**
 * Adjuster Analysis Section for Accountant Report View
 * AI-powered forensic analysis from the ADJUSTER/CARRIER perspective
 * 
 * Purpose: Help insurance adjusters identify fraud indicators, 
 * claim exaggeration, and opportunities to reduce claim payouts
 * 
 * Features:
 * - Executive summary with confidence level
 * - But-for revenue analysis with trend/seasonality
 * - Critical findings and fraud indicators
 * - Data quality assessment
 * - Cross-validation checks
 * - Actionable recommendations
 */

import React, { useState, useEffect } from 'react';
import axios from '@/lib/http';
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  CheckCircle2, 
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Shield,
  BarChart3,
  FileSearch,
  DollarSign
} from 'lucide-react';
import { formatMoneyOrNotComputed } from '../../utils/financialFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ForensicAnalysisProps {
  reportId: string;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
}

interface Finding {
  type?: string;
  title: string;
  description: string;
  severity?: string;
  evidence?: string;
  implication?: string;
  financial_impact?: number;
  recommendation?: string;
}

interface Opportunity {
  type?: string;
  title: string;
  description: string;
  financial_impact?: number;
  potential_value?: number;
  monthly_detail?: Array<{ period: string; recovery_pct: number; shortfall: number }>;
  recommendation?: string;
}

interface ButForMonth {
  period: string;
  but_for_revenue: number;
  actual_revenue: number;
  revenue_loss: number;
  seasonal_factor: number;
  recovery_pct: number;
}

interface ForensicAnalysis {
  success: boolean;
  cached: boolean;
  analysis_id?: string;
  analyzed_at?: string;
  claim_amount?: number;
  executive_summary?: string;
  claim_validity?: string;
  confidence_level?: number;
  but_for_analysis?: {
    status: string;
    pre_incident_months?: number;
    post_incident_months?: number;
    avg_monthly_revenue?: number;
    trend_factor_monthly?: number;
    seasonality_indices?: Record<string, number>;
    total_but_for_revenue?: number;
    total_actual_revenue?: number;
    total_revenue_loss?: number;
    monthly_breakdown?: ButForMonth[];
  };
  critical_findings?: Finding[];
  opportunities?: Opportunity[];
  data_quality?: {
    score: number;
    grade: string;
    months_available: number;
    issues: string[];
    reliability: string;
  };
  cross_validation?: {
    cogs_ratio_avg?: number;
    payroll_avg?: number;
    issues?: Finding[];
  };
  pattern_analysis?: {
    benford?: { status?: string; deviation?: number; issue?: boolean };
    seasonality?: {
      peak_month: number;
      peak_index: number;
      trough_month: number;
      trough_index: number;
      pattern: string;
    };
  };
  recommendations?: string[];
  financial_impact?: {
    potential_reduction: number;
    potential_increase: number;
    net_adjustment: number;
  };
  error?: string;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function ForensicAnalysisSection({
  reportId,
  getAuthHeader,
  formatCurrency
}: ForensicAnalysisProps) {
  const [analysis, setAnalysis] = useState<ForensicAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    findings: true,
    opportunities: true,
    butFor: false,
    dataQuality: false,
    patterns: false
  });

  const fetchAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/forensic/analyze/${reportId}?force_refresh=${forceRefresh}`,
        {},
        { headers: getAuthHeader() }
      );
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Forensic analysis failed:', err);
      setError(err.response?.data?.detail || 'Failed to load forensic analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [reportId]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getValidityColor = (validity?: string): React.CSSProperties => {
    switch (validity) {
      case 'VALIDATED': return { backgroundColor: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' };
      case 'LOW_RISK': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      case 'MODERATE_RISK': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      case 'HIGH_RISK': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      default: return { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', borderColor: 'var(--color-border)' };
    }
  };

  const getSeverityColor = (severity?: string): React.CSSProperties => {
    switch (severity) {
      case 'critical': return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-text-primary)' };
      case 'high': return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-text-primary)' };
      case 'medium': return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-text-primary)' };
      case 'low': return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-text-primary)' };
      default: return { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' };
    }
  };

  const getSeverityIcon = (severity?: string) => {
    switch (severity) {
      case 'critical': return <XCircle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />;
      case 'high': return <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />;
      case 'medium': return <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />;
      default: return <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />;
    }
  };

  const getReliabilityBadge = (reliability?: string) => {
    switch (reliability) {
      case 'HIGH': return <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>HIGH</span>;
      case 'MEDIUM': return <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>MEDIUM</span>;
      default: return <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>LOW</span>;
    }
  };

  if (loading && !analysis) {
    return (
      <div className="rounded-xl shadow-sm border p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3 mb-4">
          <FileSearch className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
          <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Adjuster Analysis</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 mb-4" style={{ borderColor: 'var(--color-brand)' }}></div>
            <p style={{ color: 'var(--color-text-secondary)' }}>Running forensic analysis...</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>This may take a moment</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl shadow-sm border p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3 mb-4">
          <FileSearch className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
          <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Adjuster Analysis</h2>
        </div>
        <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <p style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
          <button
            onClick={() => fetchAnalysis(true)}
            className="mt-3 px-4 py-2 rounded-lg text-sm"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  const { 
    executive_summary, 
    claim_validity, 
    confidence_level,
    but_for_analysis,
    critical_findings,
    opportunities,
    data_quality,
    pattern_analysis,
    recommendations,
    financial_impact,
    cached,
    analyzed_at
  } = analysis;

  return (
    <div className="rounded-xl shadow-sm border p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }} data-testid="adjuster-analysis-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <FileSearch className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Adjuster Analysis</h2>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {cached ? '(Cached)' : 'Fresh analysis'} • {analyzed_at ? new Date(analyzed_at).toLocaleString() : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchAnalysis(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition"
          style={{ color: 'var(--color-needs-human-text)' }}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Re-analyze
        </button>
      </div>

      {/* Executive Summary */}
      <div className="rounded-xl p-6 mb-6 border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Executive Summary</h3>
            <p style={{ color: 'var(--color-text-secondary)' }}>{executive_summary}</p>
          </div>
          <div className="flex flex-col items-end gap-2 ml-4">
            <span className="px-3 py-1.5 rounded-full text-sm font-semibold border" style={getValidityColor(claim_validity)}>
              {claim_validity?.replace('_', ' ')}
            </span>
            {confidence_level !== undefined && (
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {(confidence_level * 100).toFixed(0)}% confidence
              </span>
            )}
          </div>
        </div>

        {/* Financial Impact Summary */}
        {financial_impact && (financial_impact.potential_reduction > 0 || financial_impact.potential_increase > 0) && (
          <div className="grid grid-cols-3 gap-4 pt-4 border-t" style={{ borderColor: 'var(--color-needs-human-tint-bd)' }}>
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Potential Reduction</p>
              <p className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                {formatCurrency(financial_impact.potential_reduction)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Potential Increase</p>
              <p className="text-lg font-bold" style={{ color: 'var(--color-verified)' }}>
                {formatCurrency(financial_impact.potential_increase)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Net Adjustment</p>
              <p className="text-lg font-bold" style={{ color: financial_impact.net_adjustment >= 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                {financial_impact.net_adjustment >= 0 ? '+' : ''}{formatCurrency(financial_impact.net_adjustment)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Critical Findings */}
      {critical_findings && critical_findings.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('findings')}
            className="w-full flex items-center justify-between p-4 rounded-lg transition"
            style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                Critical Findings ({critical_findings.length})
              </span>
            </div>
            {expandedSections.findings ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.findings && (
            <div className="mt-3 space-y-3">
              {critical_findings.map((finding, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg border-l-4"
                  style={getSeverityColor(finding.severity)}
                >
                  <div className="flex items-start gap-3">
                    {getSeverityIcon(finding.severity)}
                    <div className="flex-1">
                      <h4 className="font-semibold">{finding.title}</h4>
                      <p className="text-sm mt-1">{finding.description}</p>
                      {finding.evidence && (
                        <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                          <strong>Evidence:</strong> {finding.evidence}
                        </p>
                      )}
                      {finding.financial_impact !== undefined && finding.financial_impact > 0 && (
                        <p className="text-sm mt-1 font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                          Impact: {formatCurrency(finding.financial_impact)}
                        </p>
                      )}
                      {finding.recommendation && (
                        <p className="text-sm mt-2" style={{ color: 'var(--color-needs-human-text)' }}>
                          <strong>Recommendation:</strong> {finding.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Opportunities */}
      {opportunities && opportunities.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('opportunities')}
            className="w-full flex items-center justify-between p-4 rounded-lg transition"
            style={{ backgroundColor: 'transparent' }}
          >
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>
                Opportunities to Increase Claim ({opportunities.length})
              </span>
            </div>
            {expandedSections.opportunities ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.opportunities && (
            <div className="mt-3 space-y-3">
              {opportunities.map((opp, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg border"
                  style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-verified)' }} />
                    <div className="flex-1">
                      <h4 className="font-semibold" style={{ color: 'var(--color-verified)' }}>{opp.title}</h4>
                      <p className="text-sm mt-1" style={{ color: 'var(--color-verified)' }}>{opp.description}</p>
                      {(opp.financial_impact ?? opp.potential_value) != null && (
                        <p className="text-sm mt-2 font-bold" style={{ color: 'var(--color-verified)' }}>
                          Potential Value: +{formatMoneyOrNotComputed(opp.financial_impact ?? opp.potential_value)}
                        </p>
                      )}
                      {opp.monthly_detail && opp.monthly_detail.length > 0 && (
                        <div className="mt-3 rounded p-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Monthly Recovery Detail</p>
                          <div className="space-y-1">
                            {opp.monthly_detail.map((m, i) => (
                              <div key={i} className="flex justify-between text-xs">
                                <span>{m.period}</span>
                                <span style={{ color: 'var(--color-brand)' }}>{m.recovery_pct}% recovered</span>
                                <span style={{ color: 'var(--color-needs-human-text)' }}>-{formatCurrency(m.shortfall)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {opp.recommendation && (
                        <p className="text-sm mt-2" style={{ color: 'var(--color-needs-human-text)' }}>
                          <strong>Action:</strong> {opp.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* But-For Analysis */}
      {but_for_analysis && but_for_analysis.status === 'calculated' && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('butFor')}
            className="w-full flex items-center justify-between p-4 rounded-lg transition"
            style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                But-For Revenue Analysis
              </span>
            </div>
            {expandedSections.butFor ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.butFor && (
            <div className="mt-3 rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Pre-Incident Avg</p>
                  <p className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                    {formatCurrency(but_for_analysis.avg_monthly_revenue)}/mo
                  </p>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Trend Factor</p>
                  <p className="text-lg font-bold" style={{ color: (but_for_analysis.trend_factor_monthly || 0) >= 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                    {(but_for_analysis.trend_factor_monthly || 0) >= 0 ? '+' : ''}{but_for_analysis.trend_factor_monthly}%/mo
                  </p>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Total But-For</p>
                  <p className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                    {formatCurrency(but_for_analysis.total_but_for_revenue)}
                  </p>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Revenue Loss</p>
                  <p className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                    {formatCurrency(but_for_analysis.total_revenue_loss)}
                  </p>
                </div>
              </div>

              {/* Seasonality Indices */}
              {but_for_analysis.seasonality_indices && (
                <div className="mb-4">
                  <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Seasonality Indices</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(but_for_analysis.seasonality_indices).map(([month, index]) => (
                      <div key={month} className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                        <span style={{ color: 'var(--color-text-muted)' }}>{MONTH_NAMES[parseInt(month) - 1]}:</span>
                        <span className="ml-1 font-semibold" style={{ color: index > 1.1 ? 'var(--color-verified)' : index < 0.9 ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}>
                          {(index as number).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Breakdown */}
              {but_for_analysis.monthly_breakdown && but_for_analysis.monthly_breakdown.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Post-Incident Monthly Breakdown</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm rounded" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                      <thead style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <tr>
                          <th className="px-3 py-2 text-left">Period</th>
                          <th className="px-3 py-2 text-right">But-For</th>
                          <th className="px-3 py-2 text-right">Actual</th>
                          <th className="px-3 py-2 text-right">Loss</th>
                          <th className="px-3 py-2 text-right">Recovery</th>
                        </tr>
                      </thead>
                      <tbody>
                        {but_for_analysis.monthly_breakdown.map((m, idx) => (
                          <tr key={idx} className="border-t" style={{ borderColor: 'var(--color-border-faint)' }}>
                            <td className="px-3 py-2 font-medium">{m.period}</td>
                            <td className="px-3 py-2 text-right" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(m.but_for_revenue)}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(m.actual_revenue)}</td>
                            <td className="px-3 py-2 text-right" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(m.revenue_loss)}</td>
                            <td className="px-3 py-2 text-right">
                              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={
                                m.recovery_pct >= 90 ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                                m.recovery_pct >= 70 ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                                { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                              }>
                                {m.recovery_pct}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Data Quality */}
      {data_quality && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('dataQuality')}
            className="w-full flex items-center justify-between p-4 rounded-lg transition"
            style={{ backgroundColor: 'var(--color-bg-elevated)' }}
          >
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Data Quality Assessment: {data_quality.grade}
              </span>
              {getReliabilityBadge(data_quality.reliability)}
            </div>
            {expandedSections.dataQuality ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.dataQuality && (
            <div className="mt-3 rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{data_quality.grade}</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Grade</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{data_quality.score}</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Score</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{data_quality.months_available}</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Months</p>
                </div>
              </div>

              {data_quality.issues && data_quality.issues.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Issues Found</p>
                  <ul className="space-y-1">
                    {data_quality.issues.map((issue, idx) => (
                      <li key={idx} className="text-sm flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }}></span>
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pattern Analysis */}
      {pattern_analysis && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('patterns')}
            className="w-full flex items-center justify-between p-4 rounded-lg transition"
            style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Pattern Analysis</span>
            </div>
            {expandedSections.patterns ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.patterns && (
            <div className="mt-3 rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <div className="grid grid-cols-2 gap-4">
                {/* Benford's Law */}
                {pattern_analysis.benford && (
                  <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                    <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Benford's Law Check</p>
                    {pattern_analysis.benford.status === 'passed' ? (
                      <div className="flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm">Passed ({pattern_analysis.benford.deviation}% deviation)</span>
                      </div>
                    ) : pattern_analysis.benford.issue ? (
                      <div className="flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">{pattern_analysis.benford.deviation}% deviation - investigate</span>
                      </div>
                    ) : (
                      <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Insufficient data</span>
                    )}
                  </div>
                )}

                {/* Seasonality */}
                {pattern_analysis.seasonality && (
                  <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                    <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Seasonality Pattern</p>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      <span className="font-semibold capitalize">{pattern_analysis.seasonality.pattern}</span>
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      Peak: {MONTH_NAMES[pattern_analysis.seasonality.peak_month - 1]} ({pattern_analysis.seasonality.peak_index.toFixed(2)}x) •
                      Low: {MONTH_NAMES[pattern_analysis.seasonality.trough_month - 1]} ({pattern_analysis.seasonality.trough_index.toFixed(2)}x)
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
            <Lightbulb className="w-5 h-5" />
            Recommendations
          </h3>
          <ul className="space-y-2">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                <span className="mt-1" style={{ color: 'var(--color-needs-human-text)' }}>→</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
